import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { BaseEntity } from './base.entity';
import { UserEntity } from './user.entity';

/**
 * A single push subscription bound to one user + one browser/device
 * (PD-14301 push channel). Created on `POST /api/notifications/me/push/subscribe`,
 * removed on the matching unsubscribe. The push sender fans a notification out
 * to every non-expired device row for a recipient.
 *
 * This is the TRANSPORT registry; `user_notification` is the CONTENT (the bell
 * row). A user with push enabled for an event type but no device rows simply
 * receives nothing on the push channel — the bell row is still written.
 *
 * Endpoint uniqueness is enforced per-user at the DB level
 * (`user_notification_device_user_endpoint_idx`, unique) so a browser that
 * re-subscribes upserts its existing row instead of stacking duplicates — the
 * same dedupe philosophy as `user_notification`'s dedupe key.
 */
@Index('user_notification_device_user_idx', ['fkUserId'])
@Index('user_notification_device_user_endpoint_idx', ['fkUserId', 'endpoint'], {
	unique: true,
})
@Entity('user_notification_device')
export class UserNotificationDeviceEntity extends BaseEntity {
	@Column('int', { name: 'fk_user__id', unsigned: true })
	public fkUserId!: number;

	/**
	 * Push transport / platform — `'web'` for browser Web Push today, with
	 * `'ios'` / `'android'` reserved for the later mobile ticket (PD-14302).
	 * Free-form string (no DB enum) so a new platform lands without a
	 * destructive migration; the value set is codified in a constants union.
	 */
	@Column('varchar', { name: 'platform', length: 32 })
	public platform!: string;

	/**
	 * The push service endpoint URL from the browser's `PushSubscription`.
	 * Half of the per-user unique key + the upsert target. `varchar(512)`
	 * (not `text`) so the composite unique index works without an index
	 * prefix; real push endpoints run well under this length.
	 */
	@Column('varchar', { name: 'endpoint', length: 512 })
	public endpoint!: string;

	/** `PushSubscription.keys.p256dh` — the client public key for payload encryption. */
	@Column('varchar', { name: 'p256dh', length: 255 })
	public p256dh!: string;

	/** `PushSubscription.keys.auth` — the client auth secret for payload encryption. */
	@Column('varchar', { name: 'auth', length: 255 })
	public auth!: string;

	/**
	 * Native push token (APNs / FCM) for non-web platforms (PD-14302). Null
	 * for Web Push, which uses endpoint + p256dh + auth instead.
	 */
	@Column('varchar', { name: 'deviceToken', length: 512, nullable: true })
	public deviceToken!: string | null;

	/** Browser/device user-agent captured at subscribe time, for debugging. */
	@Column('varchar', { name: 'userAgent', length: 512, nullable: true })
	public userAgent!: string | null;

	/**
	 * Last time this device was confirmed reachable (a subscribe refresh or a
	 * successful send). Drives stale-device pruning.
	 */
	@Column('datetime', { name: 'lastSeenAt' })
	public lastSeenAt!: Date;

	/**
	 * Optional expiry from the `PushSubscription.expirationTime`. A device past
	 * `expiresAt` is skipped by the sender and pruned. Null = no stated expiry.
	 */
	@Column('datetime', { name: 'expiresAt', nullable: true })
	public expiresAt!: Date | null;

	@ManyToOne(() => UserEntity, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_user__id', referencedColumnName: 'id' }])
	public fkUser!: UserEntity;
}
